import type { AuthSource } from '~/modules/user/type';

export interface AuthIntentCopy {
    /** Right-panel headline, e.g. "Create a free account to Send Enquiries". */
    headline: string;
    /** Supporting line rendered under the headline. */
    subcopy: string;
    /** Statement overlaid on the brand-pane imagery. */
    brandStatement: string;
    /**
     * The gate's intent for a reader whose email has an account: "Log in to
     * send your enquiry." Rendered under the details-step greeting, and
     * prefixed to the passkey step's line by `getPasskeyIntentCopy` — a copy
     * edit here changes both screens.
     */
    returningAction: string;
    /**
     * Details-step line for a reader whose email has no account yet:
     * "Create your free account to send your enquiry."
     */
    newAction: string;
}

const DEFAULT_BRAND_STATEMENT =
    'Get instant access to all ArchiPro features by creating a free account';

/**
 * The pane statement for a reader we recognise. Every intent's default pitches
 * account creation, which is the wrong thing to say to someone we have just
 * greeted by name and offered a one-tap login.
 */
export const RETURNING_BRAND_STATEMENT =
    'Your boards, enquiries and saved products are right where you left them';

/**
 * The passkey promise, shared by the enrolment ask and the step-0 login so the
 * two screens read as one feature. Design sets it on the enrolment frames
 * (Figma 3162:14125 and siblings); step 0 has no frame of its own and follows.
 */
export const PASSKEY_SPEED_COPY =
    'No passwords to remember, your next login will take less than a second on this device.';

/**
 * Said once, on the step the reader lands on after a passkey they had is gone
 * for good. It explains the screen they did not ask for and points at what to do
 * instead; it does not apologise, and it does not offer to try again, because
 * the credential behind that offer no longer exists.
 */
export const PASSKEY_RETIRED_NOTICE =
    'Secure Sign-In is no longer available on this device. Please log in another way.';

/**
 * The same landing after an attempt that merely failed. The passkey is still
 * there and will be tried again next time, so this says nothing about it having
 * gone.
 */
export const PASSKEY_ATTEMPT_FAILED_NOTICE =
    "Secure Sign-In didn't work this time. Please log in another way.";

const DEFAULT_SUBCOPY =
    'Manage schedules, download files and message professionals, all from one account.';

const DEFAULT_COPY: AuthIntentCopy = {
    headline: 'Create a free account to Get Started',
    subcopy: DEFAULT_SUBCOPY,
    brandStatement: DEFAULT_BRAND_STATEMENT,
    returningAction: 'Log in to continue.',
    newAction: 'Create your free account to get started.',
};

const ENQUIRY_COPY: AuthIntentCopy = {
    headline: 'Create a free account to Send Enquiries',
    subcopy:
        'Almost there, please log in or create a free account to send your message.',
    brandStatement: DEFAULT_BRAND_STATEMENT,
    returningAction: 'Log in to send your enquiry.',
    newAction: 'Create your free account to send your enquiry.',
};

const BULK_ENQUIRY_COPY: AuthIntentCopy = {
    headline: 'Create a free account to Send Bulk Enquiries',
    subcopy:
        'Almost there, please log in or create a free account to send your enquiries.',
    brandStatement: DEFAULT_BRAND_STATEMENT,
    returningAction: 'Log in to send your enquiries.',
    newAction: 'Create your free account to send your enquiries.',
};

const DOWNLOAD_COPY: AuthIntentCopy = {
    headline: 'Create a free account to Download files',
    subcopy: DEFAULT_SUBCOPY,
    brandStatement: DEFAULT_BRAND_STATEMENT,
    returningAction: 'Log in to download this file.',
    newAction: 'Create your free account to download this file.',
};

/**
 * Intent-driven copy per auth gate. Caller-passed header/subtitle always
 * override these defaults, so existing call sites keep their custom copy.
 */
const INTENT_COPY: Partial<Record<AuthSource, AuthIntentCopy>> = {
    enquiry: ENQUIRY_COPY,
    specifier: ENQUIRY_COPY,
    bulkEnquiry: BULK_ENQUIRY_COPY,
    productFiles: DOWNLOAD_COPY,
    downloadsgate: DOWNLOAD_COPY,
    designBoard: {
        headline: 'Create a free account to Save everything',
        subcopy:
            'Create boards, manage schedules, download files and message professionals, all from one account.',
        brandStatement: DEFAULT_BRAND_STATEMENT,
        returningAction: 'Log in to save this to a board.',
        newAction: 'Create your free account to save this to a board.',
    },
    articleGate: {
        headline: 'Create a free account to Keep Reading',
        subcopy: 'Enjoy unlimited access to articles, projects and products.',
        brandStatement: DEFAULT_BRAND_STATEMENT,
        returningAction: 'Log in to keep reading.',
        newAction: 'Create your free account to keep reading.',
    },
    siteGate: {
        ...DEFAULT_COPY,
        returningAction: 'Log in to continue to ArchiPro.',
        newAction: 'Create your free account to continue to ArchiPro.',
    },
    // The subscription has already landed by the time this gate opens, so the
    // copy pitches the profile on its own merits rather than as the thing
    // standing between the reader and what they came for.
    digitalPublications: {
        headline: 'Create a free account to Save what you find',
        subcopy:
            'Save projects, create Design Boards, manage Product Schedules and message professionals, all from one account.',
        brandStatement:
            'A free ArchiPro profile keeps everything you find in one place',
        returningAction: 'Log in to pick up where you left off.',
        newAction: 'Create your free account to save what you find.',
    },
    contentGate: {
        headline: 'Create a free account to Keep Browsing',
        subcopy:
            'See every product, project and professional in the catalogue, and save the ones you like.',
        brandStatement: DEFAULT_BRAND_STATEMENT,
        returningAction: 'Log in to see the rest.',
        newAction: 'Create your free account to see the rest.',
    },
    getMatched: {
        headline: 'Create a free account to Get Matched',
        subcopy: DEFAULT_SUBCOPY,
        brandStatement: DEFAULT_BRAND_STATEMENT,
        returningAction: 'Log in to get matched.',
        newAction: 'Create your free account to get matched.',
    },
    HDE: {
        ...DEFAULT_COPY,
        returningAction: 'Log in to continue your registration.',
        newAction: 'Create your free account to continue your registration.',
    },
    tradeOnlyBanner: {
        headline: 'Sign in to unlock Trade Pricing',
        subcopy:
            'Log in or create a free professional account to purchase trade-only products.',
        brandStatement: DEFAULT_BRAND_STATEMENT,
        returningAction: 'Log in to see trade pricing.',
        newAction:
            'Create your free professional account to see trade pricing.',
    },
    specialtyPricingBanner: {
        headline: 'Sign in to see Specialty Pricing',
        subcopy:
            'Log in or create a free account to view your specialty pricing.',
        brandStatement: DEFAULT_BRAND_STATEMENT,
        returningAction: 'Log in to see your specialty pricing.',
        newAction: 'Create your free account to see specialty pricing.',
    },
};

export const getAuthIntentCopy = (authSource: AuthSource): AuthIntentCopy =>
    INTENT_COPY[authSource] ?? DEFAULT_COPY;

/**
 * The passkey login step's supporting line: the gate's intent first, then the
 * speed promise that is the reason to tap. A gate whose line is the default
 * "continue" has no action behind it, so the promise stands alone rather than
 * gaining a hollow prefix — judged by the line itself, not registry
 * membership, so an entry added for its headline alone cannot change this
 * screen as a side effect.
 */
export const getPasskeyIntentCopy = (authSource: AuthSource): string => {
    const { returningAction } = getAuthIntentCopy(authSource);
    return returningAction === DEFAULT_COPY.returningAction
        ? PASSKEY_SPEED_COPY
        : `${returningAction} ${PASSKEY_SPEED_COPY}`;
};

/**
 * The recognised-reader greeting shared by the welcome-back details step and
 * the passkey login step. The account is confirmed either way, so a missing
 * stored first name degrades the greeting — never to a sign-up pitch.
 */
export const welcomeBackGreeting = (firstName?: string): string =>
    firstName ? `Welcome back, ${firstName}` : 'Welcome back';

/**
 * The brand-pane statement, which must never pitch account creation at a reader
 * we recognise. Callers pass whether the device holds a usable passkey; see
 * `matchPasskeyDeviceToHost`.
 */
export const getAuthBrandStatement = (
    authSource: AuthSource,
    isReturning: boolean,
): string =>
    isReturning
        ? RETURNING_BRAND_STATEMENT
        : getAuthIntentCopy(authSource).brandStatement;
